import { useMemo, useRef, useState } from 'react'
import { magasin } from '../lib/client/store.ts'
import { separerQuantite } from '../lib/quantite.ts'
import {
  emojiProduit,
  useMagasin,
  type LigneCourses,
  type Produit,
  type Rayon,
  type Article,
} from '../lib/client/hooks.ts'
import { Croix } from '../components/Icone.tsx'

export function useListeCourses() {
  const { etat } = useMagasin()
  return useMemo(() => {
    const produits = new Map(
      ([...etat.catalogItems.values()] as unknown as Produit[]).map((p) => [p.id, p]),
    )
    const rayons = new Map(([...etat.rayons.values()] as unknown as Rayon[]).map((r) => [r.id, r]))

    const lignes = ([...etat.shoppingItems.values()] as unknown as LigneCourses[])
      .filter((s) => !(s as unknown as { deletedAt?: unknown }).deletedAt && !s.tripId)
      .map((s) => ({
        ...s,
        nom: s.freeText ?? produits.get(s.catalogItemId ?? '')?.name ?? 'Article',
        emoji: s.catalogItemId ? emojiProduit(produits.get(s.catalogItemId), rayons) : null,
        rayon: s.rayonId ? (rayons.get(s.rayonId)?.name ?? null) : null,
      }))
      // Tri par ordre de TRAVERSÉE du magasin, pas alphabétique : on traverse
      // le magasin une fois, dans cet ordre, sans revenir sur ses pas.
      .sort((a, b) => a.rayonSortOrder - b.rayonSortOrder || a.nom.localeCompare(b.nom, 'fr'))

    /*
     * SUGGESTIONS : ce qui manque ou va manquer, SANS être sur la liste.
     *
     * « épuisé » alimentait directement la liste. Ça paraissait fermer la
     * boucle — constater le manque devant le frigo plutôt que devant une
     * liste — mais ça remplissait la liste de choses qu'on n'achète que de
     * temps en temps, qu'il fallait ensuite retirer une par une. Retirer
     * coûte autant de gestes qu'ajouter : le défaut était du mauvais côté.
     *
     * Les deux états manquants sont donc traités pareil, et c'est plus
     * cohérent qu'avant : « bientôt fini » ne proposait déjà qu'une
     * suggestion. L'épuisé passe devant, parce que « je n'en ai plus » est
     * plus pressant que « il m'en reste un peu ».
     */
    const RANG: Record<string, number> = { epuise: 0, bientot_fini: 1 }
    const suggestions = ([...etat.inventoryItems.values()] as unknown as Article[])
      .filter(
        (a) =>
          !(a as unknown as { deletedAt?: unknown }).deletedAt &&
          (a.state === 'epuise' || a.state === 'bientot_fini'),
      )
      .filter((a) => !lignes.some((l) => l.catalogItemId === a.catalogItemId))
      .map((a) => ({ article: a, produit: produits.get(a.catalogItemId) }))
      .sort(
        (x, y) =>
          (RANG[x.article.state] ?? 9) - (RANG[y.article.state] ?? 9) ||
          (x.produit?.name ?? '').localeCompare(y.produit?.name ?? '', 'fr'),
      )

    return { lignes, suggestions }
  }, [etat])
}

export function Courses({ onModeMagasin }: { onModeMagasin: () => void }) {
  const { lignes, suggestions } = useListeCourses()
  const [texte, setTexte] = useState('')
  const [deplie, setDeplie] = useState(false)

  const aPrendre = lignes.filter((l) => l.state === 'a_prendre')

  return (
    <div className="pb-28">
      <div className="sticky top-0 z-20 bg-canvas/80 backdrop-blur-xl" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex h-[var(--size-bar)] items-center justify-between px-4">
          <h1 className="font-serif text-title text-ink">Courses</h1>
          <span className="tnum text-meta text-muted">{aPrendre.length}</span>
        </div>
      </div>

      <div className="px-4">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            const v = texte.trim()
            if (!v) return
            // On tape comme on parle : « 400 g de farine » donne la ligne
            // « farine » et sa quantité, sans second champ à viser du pouce.
            const { nom, quantite } = separerQuantite(v)
            magasin.dispatch('list.add', {
              id: magasin.id(),
              freeText: nom,
              quantityText: quantite ?? undefined,
              reason: 'manuel',
            })
            setTexte('')
          }}
        >
          <input
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            placeholder="Ajouter… (ex. 400 g de farine)"
            autoCorrect="off"
            enterKeyHint="done"
            className="h-[48px] w-full rounded-sm bg-surface-sunk px-3 text-field text-ink outline-none"
          />
        </form>

        {lignes.length === 0 && (
          <p className="mt-16 text-center text-body text-muted">La liste est vide.</p>
        )}

        <ul className="mt-3 overflow-hidden rounded-md shadow-row">
          {lignes.map((l, i) => (
            <li key={l.id} className="relative">
              <div className={`flex items-center bg-surface pr-2 ${i > 0 ? 'before:absolute before:inset-x-4 before:top-0 before:h-px before:bg-line' : ''}`}>
                <button
                  type="button"
                  onClick={() =>
                    magasin.dispatch('list.check', { id: l.id, checked: l.state !== 'pris' })
                  }
                  className="pressable flex min-h-[52px] flex-1 items-center gap-3 py-2 pl-4 pr-2 text-left"
                >
                  <Coche cochee={l.state === 'pris'} />
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block truncate text-body ${l.state === 'pris' ? 'text-muted opacity-45' : 'text-ink'}`}
                    >
                      {l.emoji && <span aria-hidden className="mr-1.5">{l.emoji}</span>}
                      {l.nom}
                    </span>
                    {l.rayon && <span className="text-meta text-muted">{l.rayon}</span>}
                  </span>
                  {l.addedReason === 'stock' && (
                    <span className="text-label uppercase text-muted">épuisé</span>
                  )}
                </button>
                {l.quantityText && <Quantite id={l.id} valeur={l.quantityText} nom={l.nom} />}
                <button
                  type="button"
                  aria-label={`Retirer ${l.nom}`}
                  onClick={() => magasin.dispatch('list.remove', { id: l.id })}
                  className="pressable flex size-11 items-center justify-center text-muted"
                >
                  <Croix className="size-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>

        {suggestions.length > 0 && (
          <section className="mt-5">
            <button
              type="button"
              onClick={() => setDeplie(!deplie)}
              className="pressable flex h-9 w-full items-center justify-between px-1 text-label uppercase text-muted"
            >
              <span>Suggestions ({suggestions.length})</span>
              <span aria-hidden>{deplie ? '−' : '+'}</span>
            </button>
            {deplie && (
              <ul className="overflow-hidden rounded-md shadow-row">
                {suggestions.map(({ article, produit }, i) => (
                  <li key={article.id} className="relative">
                    <div className={`flex items-center bg-surface pr-2 ${i > 0 ? 'before:absolute before:inset-x-4 before:top-0 before:h-px before:bg-line' : ''}`}>
                      <span className="flex-1 py-3 pl-4">
                        <span className="block text-body text-ink">{produit?.name ?? 'Article'}</span>
                        {/* On DIT pourquoi c'est suggéré. Une suggestion dont
                            on ignore le motif ne se décide pas : « épuisé »
                            et « il en reste un peu » n'appellent pas la même
                            réponse. */}
                        <span
                          className={`block text-meta ${
                            article.state === 'epuise' ? 'text-epuise-ink' : 'text-bientot-ink'
                          }`}
                        >
                          {article.state === 'epuise' ? 'épuisé' : 'bientôt fini'}
                        </span>
                      </span>
                      <button
                        type="button"
                        aria-label={`Ajouter ${produit?.name ?? ''} à la liste`}
                        onClick={() =>
                          magasin.dispatch('list.add', {
                            id: magasin.id(),
                            catalogItemId: article.catalogItemId,
                            reason: 'manuel',
                          })
                        }
                        className="pressable flex size-11 items-center justify-center text-accent"
                      >
                        <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>

      {aPrendre.length > 0 && (
        <div
          className="fixed inset-x-0 z-30 px-4"
          style={{ bottom: 'calc(var(--size-bar) + env(safe-area-inset-bottom, 0px) + 12px)' }}
        >
          <button
            type="button"
            onClick={onModeMagasin}
            className="pressable h-[52px] w-full rounded-sm bg-accent text-head text-accent-ink shadow-fab"
          >
            Mode magasin · {aPrendre.length} article{aPrendre.length > 1 ? 's' : ''}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * La quantité d'une ligne, modifiable d'un tap.
 *
 * Elle est HORS du bouton qui coche la ligne : toucher le nom coche, toucher
 * la quantité l'édite, et les deux zones ne se recouvrent jamais — un bouton
 * dans un bouton est de toute façon invalide. On n'affiche rien pour une
 * ligne sans quantité (le cas courant) : un « + qté » répété sur chaque
 * ligne ferait un mur de bruit. Pour en poser une après coup, on ré-ajoute
 * le produit avec sa quantité, et elle s'ajoute à la ligne.
 *
 * Le champ fait 17 px : en dessous, Safari zoome au focus et décale toute la
 * liste.
 */
function Quantite({ id, valeur, nom }: { id: string; valeur: string; nom: string }) {
  const [edition, setEdition] = useState(false)
  // Échap doit annuler, mais il fait aussi perdre le focus : sans ce drapeau,
  // le blur qui suit enregistrerait ce qu'on voulait jeter.
  const annule = useRef(false)

  if (!edition) {
    return (
      <button
        type="button"
        aria-label={`Quantité de ${nom} : ${valeur}. Modifier`}
        onClick={() => {
          annule.current = false
          setEdition(true)
        }}
        className="pressable tnum mr-1 h-9 max-w-[40%] shrink-0 truncate rounded-sm bg-surface-sunk px-2.5 text-meta text-ink-soft"
      >
        {valeur}
      </button>
    )
  }

  return (
    <input
      autoFocus
      defaultValue={valeur}
      aria-label={`Quantité de ${nom}`}
      enterKeyHint="done"
      autoCorrect="off"
      onFocus={(e) => e.currentTarget.select()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') annule.current = true
        if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur()
      }}
      onBlur={(e) => {
        if (!annule.current && e.currentTarget.value !== valeur) {
          magasin.dispatch('list.setQuantity', { id, quantityText: e.currentTarget.value })
        }
        setEdition(false)
      }}
      className="tnum mr-1 h-9 w-28 shrink-0 rounded-sm bg-surface-sunk px-2.5 text-field text-ink outline-none"
    />
  )
}

export function Coche({ cochee, grande = false }: { cochee: boolean; grande?: boolean }) {
  const taille = grande ? 'size-14' : 'size-6'
  return (
    <span
      aria-hidden
      className={`${taille} flex shrink-0 items-center justify-center rounded-full border-2 transition-[background-color,border-color,transform] duration-[180ms] ease-(--ease-out) ${
        cochee ? 'scale-100 border-ok bg-ok' : 'border-line-strong bg-transparent'
      }`}
    >
      {cochee && (
        <svg viewBox="0 0 24 24" className={grande ? 'size-7' : 'size-4'} fill="none" stroke="white" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      )}
    </span>
  )
}
